<template>
  <DxPopup
    :visible.sync="getVisible"
    @hidden="popupHidden"
    @shown="onShown"
    @initialized="onInitialized"
    :drag-enabled="false"
    :hide-on-outside-click="popupConfig.hideOnOutsideClick"
    :show-close-button="true"
    :show-title="true"
    :max-width="'90%'"
    :width="'90%'"
    :height="popupHeight"
    :title="getTitle"
    :wrapper-attr="{
      class: 'css-v2 gc-popup gc-popup-form gc-popup-sscc-delivery',
    }"
    ref="gc-sscc-popup"
  >
    <!-- Toolbar Items -->
    <DxToolbarItem
      v-for="(item, index) in getToolbarItems"
      :key="index"
      :widget="item.widget"
      :location="item.location"
      :visible="item.visible"
      :toolbar="item.position"
      :options="item.options"
      :template="item.template ? item.template : null"
    />

    <DxScrollView>
      <!-- Header Section -->
      <div class="sscc-header-section">
        <div class="header-fields">
          <div class="header-field">
            <label class="header-label">Delivery Note</label>
            <DxTextBox
              :value="deliveryNote"
              :read-only="true"
              :disabled="true"
            />
          </div>
          <div class="header-field">
            <label class="header-label">Sales Order</label>
            <DxTextBox :value="salesOrder" :read-only="true" :disabled="true" />
          </div>
          <div class="header-field">
            <label class="header-label">Customer</label>
            <DxTextBox :value="customer" :read-only="true" :disabled="true" />
          </div>
        </div>
      </div>

      <!-- Bottom Section -->
      <div class="sscc-bottom-section">
        <!-- Top Row with Generate Button and Status -->
        <div class="bottom-top-row">
          <div class="left-actions">
            <DxButton
              :text="'Regenerate SSCC ID'"
              :icon="'refresh'"
              :type="'default'"
              :styling-mode="'contained'"
              @click="onGenerateSSCC"
              :disabled="isGenerating"
              class="generate-sscc-btn"
              v-if="isGeneratedMode"
            />
            <DxButton
              :text="'Generate SSCC ID'"
              :icon="'refresh'"
              :type="'default'"
              :styling-mode="'contained'"
              @click="onGenerateSSCC"
              :disabled="isGenerating"
              class="generate-sscc-btn"
              v-else
            />
          </div>
          <div class="right-actions">
            <!-- <div class="status-badge" :class="getStatusClass"> -->
            <!-- {{ ssccStatus }} -->
            <!-- </div> -->
            <!-- <DxButton
              :icon="'print'"
              :type="'default'"
              :styling-mode="'text'"
              @click="onPrintAll"
              :disabled="!isGeneratedMode"
              hint="Print All"
            /> -->

            <DxDropDownButton
              :items="moreOptionsItems"
              :show-arrow-icon="false"
              icon="more"
              styling-mode="text"
              hint="More Options"
              :drop-down-options="{
                width: 200,
              }"
            />
          </div>
        </div>

        <!-- Grid Section - Only visible in generated mode -->
        <div class="grid-section" v-if="isGeneratedMode && !isGenerating">
          <dx-data-grid
            :columns="getProductColumns"
            :data-source="getDataSource"
            :twoWayBindingEnabled="false"
            :repaintChangesOnly="true"
            :ref="'dx-data-grid-sscc-delivery'"
            :columnChooser="{ enabled: false }"
            :pager="{ visible: false }"
            :focusedRowEnabled="false"
            :key-expr="'ProductCode'"
            :columnAutoWidth="true"
            :height="'auto'"
            :show-borders="true"
            :scrolling="{ mode: 'standard', showScrollbar: 'never' }"
          >
            <DxSearchPanel :visible="false" />
            <DxPaging :page-size="200" :enabled="false" />
            <!-- <DxSelection mode="multiple" :show-check-boxes-mode="'always'" /> -->

            <!-- Master Detail Template - Level 1: SSCC Info -->
            <DxMasterDetail :enabled="true" template="ssccDetailTemplate" />
            <template #ssccDetailTemplate="{ data: productRow }">
              <dx-data-grid
                :columns="getSSCCColumns"
                :data-source="productRow.data.SSCCDataSource"
                :key-expr="'SSCC'"
                :twoWayBindingEnabled="false"
                :columnChooser="{ enabled: false }"
                :repaintChangesOnly="true"
                :show-borders="true"
                :width="'100%'"
                :columnAutoWidth="true"
                :scrolling="{ mode: 'standard', showScrollbar: 'never' }"
              >
                <DxPaging :page-size="200" :enabled="false" />
                <DxPager :showInfo="false" />
                <DxSearchPanel :visible="false" />

                <template #PrintButton="{ data }">
                  <DxButton
                    :icon="'print'"
                    :type="'default'"
                    :styling-mode="'text'"
                    @click="onPrintSSCC(data, productRow.data)"
                    hint="Print SSCC Label"
                  />
                </template>

                <!-- Master Detail Template - Level 2: Batch Info -->
                <DxMasterDetail
                  :enabled="true"
                  template="batchDetailTemplate"
                />
                <template #batchDetailTemplate="{ data: ssccRow }">
                  <dx-data-grid
                    :columns="getBatchColumns"
                    :data-source="ssccRow.data.BatchDataSource"
                    :key-expr="'BatchNo'"
                    :twoWayBindingEnabled="false"
                    :columnChooser="{ enabled: false }"
                    :repaintChangesOnly="true"
                    :show-borders="true"
                    :columnAutoWidth="true"
                    :scrolling="{ mode: 'standard', showScrollbar: 'never' }"
                    :width="'100%'"
                  >
                    <DxPaging :page-size="200" :enabled="false" />
                    <DxPager :showInfo="false" />
                    <DxSearchPanel :visible="false" />

                    <template #DateTemplate="{ data }">
                      <span>{{
                        data.value ? formatDate(data.value) : ""
                      }}</span>
                    </template>

                    <template #Decimal="{ data }">
                      <span>{{ formatDecimal(data.value) }}</span>
                    </template>
                  </dx-data-grid>
                </template>
              </dx-data-grid>
            </template>

            <template #Decimal="{ data }">
              <span>{{ formatDecimal(data.value) }}</span>
            </template>
          </dx-data-grid>
        </div>
      </div>
    </DxScrollView>
  </DxPopup>
</template>

<script>
import DataGridMixin from "@/_mixins/data-grid.mixin";
import ArrayStore from "devextreme/data/array_store";
import ColumnWidth from "../_helpers/data-grid/ColumnWidth";
import { DeliveryNoteSSCCService } from "../_services/api/sales/delivery-note-sscc.service";
import { DxButton } from "devextreme-vue/button";
import { DxTextBox } from "devextreme-vue/text-box";
import { DxDropDownButton } from "devextreme-vue/drop-down-button";

export default {
  mixins: [DataGridMixin],
  components: {
    DxButton,
    DxTextBox,
    DxDropDownButton,
  },
  props: {
    setVisible: {
      type: Boolean,
      default: false,
    },
    hidden: {
      type: Function,
      default: () => {},
    },
    showLoading: {
      type: Function,
      default: function () {},
    },
    dataObj: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      dataSource: new ArrayStore({
        key: "ProductCode",
        data: [],
      }),
      deliveryNote: null,
      salesOrder: null,
      customer: null,
      isGeneratedMode: false,
      isGenerating: false,
      ssccStatus: "Pending",
      // popupHeight: "80%",
      popupConfig: {
        isPopupShow: false,
        title: "",
        formMode: "review",
        hideOnOutsideClick: false,
      },
    };
  },

  async created() {
    // Initialize from dataObj passed from parent (similar to sample-popup.vue)
    if (this.dataObj) {
      this.deliveryNote = this.dataObj.DeliveryNote.toString();
      this.salesOrder = this.dataObj.SalesOrderNo.toString();
      this.customer = this.dataObj.CustomerName.toString();
    }
  },

  computed: {
    getVisible: {
      get() {
        return this.setVisible;
      },
      set(val) {
        if (!val) {
          this.hidden();
        }
      },
    },

    popupHeight() {
      const hasData =
        this.dataSource &&
        this.dataSource._array &&
        this.dataSource._array.length > 0;
      return hasData ? "80%" : "auto";
    },

    moreOptionsItems() {
      return [
        {
          text: "Print All",
          icon: "print",
          disabled: !this.isGeneratedMode,
          onClick: () => this.onPrintAll(),
        },
      ];
    },

    getTitle() {
      return `SSCC ID Delivery #${this.deliveryNote} for Sales Order #${this.salesOrder} - ${this.customer}`;
    },

    getStatusClass() {
      return {
        "status-active": this.ssccStatus === "Active",
        "status-pending": this.ssccStatus === "Pending",
        "status-generated": this.ssccStatus === "Generated",
      };
    },

    getToolbarItems() {
      return [
        {
          widget: "dxButton",
          position: "before",
          location: "before",
          visible: true,
          options: {
            icon: "close",
            stylingMode: "text",
            onClick: this.onCancelPopupHandler,
          },
        },
      ];
    },

    getDataSource() {
      return this.dataSource;
    },

    getProductColumns() {
      return [
        {
          caption: "",
          dataField: "",
          type: "detailExpand",
          fixed: true,
          fixedPosition: "left",
          width: 30,
          minWidth: 30,
        },
        // {
        //   caption: "",
        //   dataField: "",
        //   type: "selection",
        //   fixed: true,
        //   fixedPosition: "left",
        //   width: 40,
        // },
        {
          caption: "Product",
          dataField: "ProductDescription",
          allowEditing: false,
          minWidth: 350,
          cellTemplate: (container, options) => {
            container.innerHTML = `
              <div class="product-cell">
                <div class="product-description">${options.data.ProductDescription}</div>
                <div class="product-code">${options.data.ProductCode}</div>
              </div>
            `;
          },
        },
        {
          caption: "UM",
          dataField: "UM",
          allowEditing: false,
          width: ColumnWidth.UnitOfMeasure.width,
          alignment: "center",
        },
        {
          caption: "Delivered Quantity",
          dataField: "StockUMQty",
          allowEditing: false,
          dataType: "number",
          format: "#,##0.0000",
          width: 150,
          alignment: "right",
          // cellTemplate: "Decimal",
        },
        {
          caption: "",
          dataField: "",
          type: "",
          width: 10,
        },
      ];
    },

    getSSCCColumns() {
      return [
        {
          caption: "",
          dataField: "",
          type: "detailExpand",
          width: 30,
          minWidth: 30,
        },
        {
          caption: "SSCC",
          dataField: "SSCC",
          allowEditing: false,
          minWidth: 250,
        },
        {
          caption: "Quantity",
          dataField: "StockUMQty",
          allowEditing: false,
          dataType: "number",
          format: "#,##0.0000",
          width: 150,
          alignment: "right",
          // cellTemplate: "Decimal",
        },
        {
          caption: "",
          dataField: "Print",
          allowEditing: false,
          width: 60,
          alignment: "center",
          cellTemplate: "PrintButton",
        },
      ];
    },

    getBatchColumns() {
      return [
        {
          caption: "Batch",
          dataField: "BatchNo",
          allowEditing: false,
          width: 150,
        },
        {
          caption: "Supplier",
          dataField: "Supplier",
          allowEditing: false,
          minWidth: 200,
          cellTemplate: (container, options) => {
            const supplierCode = options.data.SupplierCode || "";
            container.innerHTML = `
              <div class="supplier-cell">
                <div class="supplier-name">${options.data.SupplierName}</div>
                ${
                  supplierCode
                    ? `<div class="supplier-code">${supplierCode}</div>`
                    : ""
                }
              </div>
            `;
          },
        },
        {
          caption: "Use By / Best Before",
          dataField: "UseByBestBefore",
          allowEditing: false,
          dataType: "date",
          width: 180,
          alignment: "center",
          cellTemplate: "DateTemplate",
        },
        {
          caption: "Quantity",
          dataField: "StockUMQty",
          allowEditing: false,
          dataType: "number",
          format: "#,##0.0000",
          width: 150,
          alignment: "right",
          cellTemplate: "Decimal",
        },
      ];
    },
  },

  methods: {
    onInitialized(e) {
      e.component.registerKeyHandler("escape", (arg) => {
        arg.stopPropagation();
        this.onCancelPopupHandler();
      });
    },

    async onShown() {
      // Load existing records when popup is shown
      await this.loadSSCCRecords();
    },

    async loadSSCCRecords() {
      try {
        this.showLoading(true);
        this.isGenerating = true;

        const response =
          await DeliveryNoteSSCCService.getDeliveryNoteSSCCRecords(
            this.deliveryNote
          );

        if (response && response.data && response.data.length > 0) {
          // Process and load the data
          this.processAndLoadData(response.data);
          this.isGeneratedMode = true;
        } else {
          // No data - show empty state
          this.isGeneratedMode = false;
          this.dataSource = new ArrayStore({
            key: "ProductCode",
            data: [],
          });
        }
      } catch (error) {
        this.$notify("Error loading SSCC records", "error");
        this.isGeneratedMode = false;
      } finally {
        this.isGenerating = false;
        this.showLoading(false);
      }
    },

    processAndLoadData(data) {
      // The API returns data in the same format as mock data
      // So we can directly use it
      this.dataSource = new ArrayStore({
        key: "ProductCode",
        data: data,
      });
    },

    async onGenerateSSCC() {
      try {
        this.showLoading(true);
        this.isGenerating = true;

        // Call generate API
        await DeliveryNoteSSCCService.generateSSCC(
          this.deliveryNote
        );

        // Reload the data after generation
        const response =
          await DeliveryNoteSSCCService.getDeliveryNoteSSCCRecords(
            this.deliveryNote
          );

        if (response && response.data && response.data.length > 0) {
          this.processAndLoadData(response.data);
          this.isGeneratedMode = true;
        } else {
          this.$notify("No SSCC records found after generation", "warning");
          this.isGeneratedMode = false;
        }
      } catch (error) {
        this.$alert(error.data[0].ErrorMessage, "Error");
      } finally {
        this.isGenerating = false;
        this.showLoading(false);
      }
    },

    onPrintSSCC(e, productData) {
      this.showLoading(true);
      DeliveryNoteSSCCService.printDeliveryNoteSSCC({
        SSCC: e.data.SSCC,
        DeliveryNote: this.deliveryNote,
        DeliveryNoteLineNo: productData.DeliveryNoteLineNo,
      })
        .then((result) => {
          if (result) this.showLoading(false);
          this.$notify(`Successfully queued SSCC label printing`, "success");
        })
        .catch((error) => {
          this.$alert(error, "Print SSCC Labels Failed");
          this.showLoading(false);
        });
    },

    onPrintAll() {
      this.showLoading(true);
      DeliveryNoteSSCCService.printDeliveryNoteSSCC({
        SSCC: undefined,
        DeliveryNote: this.deliveryNote,
        DeliveryNoteLineNo: undefined,
      })
        .then((result) => {
          if (result) this.showLoading(false);
          this.$notify(
            `Successfully queued Delivery Note ${this.deliveryNote} for SSCC label printing`,
            "success"
          );
        })
        .catch((error) => {
          this.showLoading(false);
          this.displayServerError(error, "Print SSCC Labels Failed");
        });
    },

    formatDate(date) {
      if (!date) return "";
      const d = new Date(date);
      const day = String(d.getDate()).padStart(2, "0");
      const month = d.toLocaleString("en-US", { month: "short" });
      const year = d.getFullYear();
      return `${day} ${month} ${year}`;
    },

    formatDecimal(value) {
      if (value === null || value === undefined) return "";
      return parseFloat(value).toFixed(4);
    },

    async onCancelPopupHandler() {
      this.popupHidden();
    },

    popupHidden() {
      this.hidden();
    },
  },
};
</script>

<style lang="scss">
.gc-popup-sscc-delivery {
  .sscc-header-section {
    padding: 16px 20px;
    background-color: #f5f5f5;
    border-bottom: 1px solid #e0e0e0;

    .header-fields {
      display: flex;
      gap: 24px;
      align-items: center;
    }

    .header-field {
      flex: 1;
      display: flex;
      align-items: center;
      gap: 12px;

      .header-label {
        font-size: 13px;
        color: #333;
        font-weight: 500;
        white-space: nowrap;
        min-width: 100px;
      }

      .dx-textbox {
        flex: 1;

        .dx-texteditor-input {
          background-color: #fff;
          font-weight: 600;
        }
      }
    }
  }

  .sscc-bottom-section {
    padding: 16px 20px;

    .bottom-top-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 8px;
      padding-bottom: 8px;
      border-bottom: 1px solid #e0e0e0;

      .left-actions {
        display: flex;
        gap: 12px;
        align-items: center;
      }

      .right-actions {
        display: flex;
        gap: 12px;
        align-items: center;

        .status-badge {
          padding: 6px 16px;
          border-radius: 4px;
          font-size: 13px;
          font-weight: 600;
          text-transform: uppercase;

          &.status-active {
            background-color: #4caf50;
            color: white;
          }

          &.status-pending {
            background-color: #ff9800;
            color: white;
          }

          &.status-generated {
            background-color: #2196f3;
            color: white;
          }
        }
      }
    }

    .generate-sscc-btn {
      background-color: #03a9f4 !important;
      color: white !important;

      .dx-icon {
        color: white !important;
      }

      .dx-icon::before {
        color: unset;
      }
    }

    .grid-section {
      margin-top: 8px;

      .product-cell {
        .product-description {
          font-size: 14px;
          color: #333;
          font-weight: 500;
        }

        .product-code {
          font-size: 12px;
          color: #666;
          margin-top: 2px;
        }
      }

      .supplier-cell {
        .supplier-name {
          font-size: 13px;
          color: #333;
        }

        .supplier-code {
          font-size: 11px;
          color: #666;
          margin-top: 2px;
        }
      }
    }

    .loading-section {
      display: flex;
      justify-content: center;
      align-items: center;
      min-height: 300px;

      .loading-spinner {
        text-align: center;

        i {
          font-size: 48px;
          color: #03a9f4;
          animation: spin 1s linear infinite;
        }

        p {
          margin-top: 16px;
          font-size: 14px;
          color: #666;
        }
      }
    }

    .empty-state {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      min-height: 300px;
      color: #999;

      i {
        font-size: 64px;
        margin-bottom: 16px;
        opacity: 0.5;
      }

      p {
        font-size: 16px;
        margin: 8px 0;
      }

      .empty-state-hint {
        font-size: 14px;
        color: #aaa;
      }
    }
  }

  // Nested grid styling
  .dx-datagrid-rowsview .dx-master-detail-cell {
    padding: 12px;
    background-color: red;
  }

  // Level 2 nested grid (batch info)
  .dx-datagrid .dx-datagrid .dx-master-detail-cell {
    background-color: #f5f5f5;
    padding: 10px 15px !important;
  }

  .dx-datagrid {
    .dx-row.dx-data-row {
      &:hover {
        background-color: #f9f9f9;
      }
    }
  }
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
